/**
 * <s-tier-quiz>: "Which one is me?" above the compare table. Three radio questions,
 * each answer votes for a tier (value="essential" | "premium").
 * - Live: [data-quiz-step] dots fill per answer; the meter knob leans by score
 *   (--lean from -1 Essential to 1 Premium); host gets data-answered="<n>".
 * - Result (all answered): host data-rec="<tier>"; [data-rec-ring] is laid over the
 *   whole recommended column (header to CTA) and bursts sparks; [data-quiz-result]
 *   announces the reason (data-msg-<tier>); the table scrolls into view if needed.
 * - [data-quiz-reset] clears everything. Reduced motion: no sparks, no smooth scroll.
 */
class STierQuiz extends HTMLElement {
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #resize = null;
  #onChange = () => this.#update(true);
  #onClick = (event) => {
    if (!event.target.closest('[data-quiz-reset]')) return;
    for (const input of this.querySelectorAll('input[type="radio"]')) input.checked = false;
    this.#update(false);
    this.querySelector('input[type="radio"]')?.focus();
  };

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    const table = this.querySelector('table');
    if (table && 'ResizeObserver' in window) {
      this.#resize = new ResizeObserver(() => this.#ring());
      this.#resize.observe(table);
      // The panel also holds the segment row above the table; watch it so the ring follows when that row
      // changes height (fonts, accordion on small screens) and pushes the table down.
      const panel = this.querySelector('[data-rec-ring]')?.parentElement;
      if (panel) this.#resize.observe(panel);
      const row = this.querySelector('[data-launch-row]');
      if (row) this.#resize.observe(row);
      row?.addEventListener('transitionend', () => this.#ring());
    }
    this.querySelector('[data-quiz-scroll]')?.addEventListener('scroll', () => this.#ring(), { passive: true });
    this.#update(false);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.#resize?.disconnect();
  }

  #update(fromUser) {
    const inputs = [...this.querySelectorAll('input[type="radio"]')];
    const answers = inputs.filter((input) => input.checked).map((input) => input.value);
    const groups = new Set(inputs.map((input) => input.name)).size;
    const premium = answers.filter((value) => value === 'premium').length;
    const score = premium - (answers.length - premium);
    this.dataset.answered = String(answers.length);
    this.style.setProperty('--lean', String(groups ? score / groups : 0));
    this.querySelectorAll('[data-quiz-step]').forEach((dot, index) => dot.toggleAttribute('data-done', index < answers.length));

    const result = this.querySelector('[data-quiz-result]');
    const reset = this.querySelector('[data-quiz-reset]');
    if (reset) reset.hidden = answers.length === 0;
    if (answers.length < groups) {
      this.removeAttribute('data-rec');
      if (result) result.textContent = '';
      return;
    }
    const tier = premium * 2 > answers.length ? 'premium' : 'essential';
    const changed = this.dataset.rec !== tier;
    this.dataset.rec = tier;
    if (result) result.textContent = result.dataset[`msg${tier[0].toUpperCase()}${tier.slice(1)}`] ?? '';
    this.#ring();
    if (fromUser && changed) this.#celebrate();
    if (fromUser) {
      try { sessionStorage.setItem("the4-tier", tier); } catch {}
      document.dispatchEvent(new CustomEvent("the4:tier", { detail: tier }));
    }
  }

  /**
   * Lay the ring over the recommended column (header to CTA) and pin the "Best fit for you" tag on the
   * centre of its top border. Both live in the table's outer frame (not the scroller) so the tag can sit
   * on the border; the ring is clamped to the visible part when the table scrolls sideways on small screens.
   * Corners are rounded only where the column meets the frame's outer edge.
   */
  #ring() {
    const ring = this.querySelector('[data-rec-ring]');
    const tag = this.querySelector('[data-rec-tag]');
    const wrap = ring?.parentElement;
    const tier = this.dataset.rec;
    if (!ring || !wrap || !tier) return;
    const cells = [...this.querySelectorAll(`table [data-col="${tier}"]`)];
    if (!cells.length) return;
    const base = wrap.getBoundingClientRect();
    let first = cells[0].getBoundingClientRect();
    // From lg the segment heading of this column sits right above it (same left edge): start the ring there.
    const head = this.querySelector(`[data-launch-row] [data-opt="${tier}"]`)?.getBoundingClientRect();
    if (head && head.width > 0 && Math.abs(head.left - first.left) < 4 && head.top < first.top) first = head;
    const last = cells[cells.length - 1].getBoundingClientRect();
    const inner = wrap.clientWidth;
    const rawLeft = first.left - base.left - wrap.clientLeft;
    const left = Math.max(0, rawLeft);
    const right = Math.min(inner, rawLeft + first.width);
    const top = first.top - base.top - wrap.clientTop;
    ring.toggleAttribute('data-edge-left', left < 2);
    ring.toggleAttribute('data-edge-right', inner - right < 2);
    ring.style.setProperty('--rx', `${left}px`);
    ring.style.setProperty('--ry', `${top}px`);
    ring.style.setProperty('--rw', `${Math.max(0, right - left)}px`);
    ring.style.setProperty('--rh', `${last.bottom - first.top}px`);
    tag?.style.setProperty('--tx', `${(left + right) / 2}px`);
    tag?.style.setProperty('--ty', `${top}px`);
  }

  #celebrate() {
    const ring = this.querySelector('[data-rec-ring]');
    if (!ring) return;
    ring.removeAttribute('data-pop');
    void ring.offsetWidth;
    ring.setAttribute('data-pop', '');
    if (!this.#reduced) {
      const wrap = ring.parentElement;
      wrap.querySelectorAll('[data-spark]').forEach((spark) => spark.remove());
      const cx = parseFloat(ring.style.getPropertyValue('--rx')) + parseFloat(ring.style.getPropertyValue('--rw')) / 2;
      const cy = parseFloat(ring.style.getPropertyValue('--ry'));
      for (let i = 0; i < 14; i += 1) {
        const spark = document.createElement('span');
        spark.setAttribute('data-spark', '');
        const angle = (Math.PI * 2 * i) / 14;
        spark.style.setProperty('--sx', `${Math.cos(angle) * (60 + (i % 3) * 22)}px`);
        spark.style.setProperty('--sy', `${Math.sin(angle) * (30 + (i % 3) * 12)}px`);
        spark.style.left = `${cx}px`;
        spark.style.top = `${cy}px`;
        spark.style.setProperty('--sd', `${(i % 4) * 40}ms`);
        wrap.append(spark);
        spark.addEventListener('animationend', () => spark.remove(), { once: true });
      }
    }
    const table = this.querySelector('[data-quiz-table]');
    const box = table?.getBoundingClientRect();
    if (box && (box.top > window.innerHeight * 0.6 || box.bottom < 0)) {
      table.scrollIntoView({ behavior: this.#reduced ? 'auto' : 'smooth', block: 'center' });
    }
  }
}

if (!customElements.get('s-tier-quiz')) customElements.define('s-tier-quiz', STierQuiz);
